import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'

interface CounterProps {
    value: string
    className?: string
    duration?: number
}

/* Counts the numeric part of a value up on entry and leaves any prefix or
   suffix in place, so "24hr", "92%", "+47%" and "$4,500" all behave. */
export const Counter: FC<CounterProps> = ({ value, className, duration = 1.8 }): ReactNode => {
    const scope = useRef<HTMLSpanElement>(null)

    useGsap(() => {
        const node = scope.current?.querySelector<HTMLElement>('[data-counter-number]')

        if (!node) {
            return
        }

        const raw = node.dataset.counterNumber ?? '0'
        const grouped = raw.includes(',')
        const target = Number(raw.replace(/,/g, ''))

        if (Number.isNaN(target) || prefersReducedMotion()) {
            return
        }

        const proxy = { value: 0 }

        gsap.to(proxy, {
            value: target,
            duration,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: scope.current,
                start: 'top 88%',
                once: true
            },
            onUpdate: () => {
                const current = Math.round(proxy.value)

                node.textContent = grouped
                    ? current.toLocaleString('en-US')
                    : String(current)
            }
        })
    }, scope, [value])

    const match = value.match(/^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/)

    if (!match) {
        return (
            <span ref={scope} className={cn(className)}>
                {value}
            </span>
        )
    }

    const [, prefix, number, suffix] = match

    return (
        <span ref={scope} className={cn('tabular-nums', className)}>
            {prefix}

            <span data-counter-number={number}>
                {number}
            </span>

            {suffix}
        </span>
    )
}
